<template>
  <div class="chart-wrapper print-chart-wrapper mt-15">
    <LoadingAnimation v-if="loading" />
    <div class="chart-content" v-else>
      <div class="hour-title">
        <span class="hour-item pt-2 pb-2" :class="{ active: timeIconActive }">
          <i class="el-icon-time bold-font font-18"></i>
        </span>
        <span
          v-for="hour in hours"
          :key="hour.value"
          class="hour-item pt-2 pb-2"
          :class="hour.active ? 'active' : ''"
          >{{ hour.show ? hour.label : '' }}
        </span>
      </div>
      <div
        v-for="(day, index) in calculatedData"
        :key="day.Date"
        class="day-item"
      >
        <div class="day-title">
          <div>Day {{ index + 1 }}</div>
          <div>
            {{
              userLang.indexOf('en') > -1
                ? moment(day.Date).format('DD MMM YY')
                : moment(day.Date).format('DD/MM/YY')
            }}
          </div>
        </div>
        <div class="day-icon-content" v-if="day.Data.length">
          <!-- pad cycle -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday"
            class="day-icon-item"
            :style="{
              left: (dayItem.StartToToday / dayDuration) * 100 + '%',
              width: (dayItem.BlockWidth / dayDuration) * 100 + '%'
            }"
          >
            <PadCyclePopover
              :dayItem="dayItem"
              :calculatedData="calculatedData"
              :index="index"
            />
          </div>
          <!-- smartz events -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday + 'event'"
            class="events"
          >
            <div
              v-for="(item, index) in dayItem.Events"
              :key="item.StartToToday + item.Status"
            >
              <!-- smartz event duration -->
              <div
                v-if="item.Duration"
                :style="{
                  left: getEventPosition(dayItem, index),
                  width: (item.Duration / dayDuration) * 100 + '%',
                  backgroundColor: [
                    smartzEventConst.external,
                    smartzEventConst.clumped
                  ].includes(item.Status)
                    ? '#FFE083'
                    : '#546E7A'
                }"
                class="smartz-event-duration"
              ></div>

              <!-- smartz event icon -->
              <el-tooltip
                placement="top"
                effect="light"
                popper-class="event-detail-popup"
              >
                <!-- poper content -->
                <div slot="content">
                  <div class="event-item">
                    <div
                      class="smart-event-icon left-side"
                      :class="getEventInfo(item.Status).class"
                    >
                      <img
                        v-if="getEventInfo(item.Status).imgSrc"
                        :src="getEventInfo(item.Status).imgSrc"
                      />
                    </div>
                    <div class="inline-block position-relative ">
                      <div class="status-text secondary-font">
                        {{
                          getEventInfo(item.Status).text | firstLetterCapitalize
                        }}
                      </div>
                      <div>{{ moment(item.TimeStamp).format('h:mm A') }}</div>
                      <div v-if="item.Duration">
                        <div class="status-text mt-5 secondary-font">
                          {{ $t('text.duration') }}
                        </div>
                        <div>{{ item.Duration | secondFormat }}</div>
                      </div>
                      <div v-if="item.IsFirstGreen">
                        <div
                          v-if="dayItem.IsValidTemp === 0"
                          class="invalid-temperature-icon v-h-center"
                        >
                          <img
                            :src="
                              require('@/assets/image/VoidingPatternIcon/invalid-temperature.png')
                            "
                          />
                        </div>
                        <div v-if="dayItem.ProductName">
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.product_name') }}
                          </div>
                          <div>
                            <span>{{ dayItem.ProductName }}</span
                            >&nbsp;
                            <span v-if="dayItem.PadCycleType"
                              >({{ dayItem.PadCycleType }})</span
                            >
                          </div>
                        </div>
                        <div>
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.predicted_time') }}
                          </div>
                          <div>
                            {{
                              Number(dayItem.PredictedTime) &&
                              dayItem.PredictedTimeFormatted
                                ? dayItem.PredictedTimeFormatted
                                : 'N/A'
                            }}
                          </div>
                        </div>
                        <div v-if="dayItem.RedSensor">
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.max_sensor') }}
                          </div>
                          <div>{{ dayItem.RedSensor }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="event-item"
                    v-for="subEvent in item.SubEvents"
                    :key="subEvent.TimeStamp + subEvent.Status"
                  >
                    <div
                      class="smart-event-icon left-side"
                      :class="getEventInfo(subEvent.Status).class"
                    >
                      <img
                        v-if="getEventInfo(subEvent.Status).imgSrc"
                        :src="getEventInfo(subEvent.Status).imgSrc"
                      />
                    </div>
                    <div class="inline-block position-relative ">
                      <div class="status-text secondary-font">
                        {{
                          getEventInfo(subEvent.Status).text
                            | firstLetterCapitalize
                        }}
                      </div>
                      <div>
                        {{ moment(subEvent.TimeStamp).format('h:mm A') }}
                      </div>
                      <div v-if="subEvent.IsFirstGreen">
                        <div
                          v-if="dayItem.IsValidTemp === 0"
                          class="invalid-temperature-icon v-h-center"
                        >
                          <img
                            :src="
                              require('@/assets/image/VoidingPatternIcon/invalid-temperature.png')
                            "
                          />
                        </div>
                        <div v-if="dayItem.ProductName">
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.product_name') }}
                          </div>
                          <div>
                            <span>{{ dayItem.ProductName }}</span
                            >&nbsp;
                            <span v-if="dayItem.PadCycleType"
                              >({{ dayItem.PadCycleType }})</span
                            >
                          </div>
                        </div>
                        <div>
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.predicted_time') }}
                          </div>
                          <div>
                            {{
                              Number(dayItem.PredictedTime) &&
                              dayItem.PredictedTimeFormatted
                                ? dayItem.PredictedTimeFormatted
                                : 'N/A'
                            }}
                          </div>
                        </div>
                        <div v-if="dayItem.RedSensor">
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.max_sensor') }}
                          </div>
                          <div>{{ dayItem.RedSensor }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- There are multiple events near this icon, which are displayed in combination -->
                <!-- only show the top icon and the bottom icon's edge in the chart -->
                <div
                  v-if="item.SubEvents.length"
                  class="stack-icon-wrapper-in-tooltip"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                >
                  <!-- bottom icon's edge -->
                  <div
                    class="smart-event-icon"
                    :class="getEventInfo(item.Status).class"
                  ></div>
                  <!-- top icon -->
                  <div
                    class="smart-event-icon top-icon"
                    :class="getEventInfo(topEventStatus(item.SubEvents)).class"
                  >
                    <img
                      v-if="getEventInfo(topEventStatus(item.SubEvents)).imgSrc"
                      :src="getEventInfo(topEventStatus(item.SubEvents)).imgSrc"
                    />
                  </div>
                </div>
                <!-- Single event -->
                <div
                  v-else
                  class="smart-event-icon"
                  :class="getEventInfo(item.Status).class"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                >
                  <img
                    v-if="getEventInfo(item.Status).imgSrc"
                    :src="getEventInfo(item.Status).imgSrc"
                  />
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { images, voidState, hours } from '@/const.js'
import {
  smartzEventConst,
  padCycleEndType,
  padCycleStartType
} from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
import LoadingAnimation from '@/components/patients/charts/LoadingAnimation'
import PadCyclePopover from '@/components/patients/charts/smartzEventsChart/PadCyclePopover.vue'
export default {
  data() {
    return {
      images,
      voidState,
      moment,
      userLang: navigator.language || navigator.userLanguage,
      hours,
      dayDuration: 86400,
      minEventDuration: 5, // Minimum event interval in minutes
      smartzEventConst,
      padCycleEndType,
      padCycleStartType,
      calculatedData: [],
      eventDistanceLessShouldCombine: 30,
      notCombinedEventStatus: [
        smartzEventConst.external,
        smartzEventConst.clumped,
        smartzEventConst.outOfRange
      ]
    }
  },
  props: {
    chartData: {
      type: Array,
      require: true
    },
    loading: {
      type: Boolean,
      require: true
    },
    timeIconActive: {
      type: Boolean,
      default: false
    }
  },
  components: {
    LoadingAnimation,
    PadCyclePopover
  },
  computed: {
    getEventInfo() {
      return type => {
        let info
        switch (type) {
          case this.smartzEventConst.analysing:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/analysing-pad.png'),
              class: 'analysing-pad',
              text: this.$t('text.analysing_diaper_legend', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.green:
            info = {
              imgSrc: '',
              class: 'green-pad',
              text: this.$t('text.dry_diaper', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.yellow:
            info = {
              imgSrc: '',
              class: 'yellow-pad',
              text: this.$t('text.wet_diaper', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.red:
            info = {
              imgSrc: '',
              class: 'red-pad',
              text: this.$t('text.change_diaper', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.outOfRange:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/out-of-range.png'),
              class: 'out-of-range',
              text: this.$t('text.out_of_range')
            }
            break
          case this.smartzEventConst.lowBattery:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/low-battery.png'),
              class: 'low-battery',
              text: this.$t('text.low_battery')
            }
            break
          case this.smartzEventConst.goalTimeExceed:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/time-to-change.png'),
              class: 'time-to-change',
              text: this.$t('text.exceed_goal_time')
            }
            break
          case this.smartzEventConst.fall:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/fall.png'),
              class: 'fall',
              text: this.$t('text.fall')
            }
            break
          case this.smartzEventConst.reposition:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/reposition.png'),
              class: 'reposition',
              text: this.$t('text.reposition')
            }
            break
          case this.smartzEventConst.temperature:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/temperature.png'),
              class: 'temperature',
              text: this.$t('text.temperature')
            }
            break
          case this.smartzEventConst.external:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/external-humidity.png'),
              class: 'external-humidity',
              text: this.$t('text.external_humidity')
            }
            break
          case this.smartzEventConst.clumped:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/core-breakup.png'),
              class: 'external-humidity',
              text:
                this.$t('text.core_breakup') +
                ' / ' +
                this.$t('text.wetness_outside')
            }
            break
          case this.smartzEventConst.clumpedGreen:
          case this.smartzEventConst.clumpedYellow:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/core-breakup.png'),
              class: 'external-humidity',
              text:
                this.$t('text.core_breakup') +
                ' / ' +
                this.$t('text.wetness_outside')
            }
            break
          case this.smartzEventConst.padRemoved:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-removed.svg'),
              class: 'pad-removed',
              text: this.$t('text.diaper_removed_legend', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.diaperNotSelected:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/diaper-not-selected.png'),
              class: 'diaper-not-selected',
              text: this.$t('text.diaper_not_selected', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.allowedTimeExceed:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/predicted-change-time.svg'),
              class: 'predicted-change-time',
              text: this.$t('text.predicted_change_time')
            }
            break
          case this.smartzEventConst.predictedRed:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/predicted-change-time.svg'),
              class: 'predicted-change-time',
              text: this.$t('text.predicted_red')
            }
            break
          case this.smartzEventConst.safeTimeExceed:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/exceed-safe-time-in-pad.svg'),
              class: 'exceed-safe-time-in-pad',
              text: this.$t('text.exceed_safe_time_in_pad', {
                pad: this.$store.state.locale.pad
              })
            }
            break
          case this.smartzEventConst.newBattery:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/new-battery.svg'),
              class: 'new-battery',
              text: this.$t('text.new_battery')
            }
            break
          case this.smartzEventConst.checkBin:
            info = {
              imgSrc: require('@/assets/image/NotificationIcons/pad-in-bin.png'),
              class: 'check-bin',
              text: this.$t('text.check_bin')
            }
            break
          case this.smartzEventConst.doubleTap:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/double-tap.svg'),
              class: 'double-tap',
              text: this.$t('text.double_tap')
            }
            break
          case this.smartzEventConst.leakage:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/leakage.svg'),
              class: 'leakage',
              text: this.$t('text.leakage')
            }
            break
          case this.smartzEventConst.unstable:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/unstable.png'),
              class: 'unstable',
              text: this.$t('text.monitor_unstable_readings')
            }
            break
          case this.smartzEventConst.diaperNotUsed:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/diaper-not-used.png'),
              class: 'diaper-not-selected',
              text: this.$t('text.brief_not_used', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.smartzEventConst.sensorDisconnected:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/sensor-disconnected.png'),
              class: 'sensor-disconnected',
              text: this.$t('text.sensor_disconnected')
            }
            break
          case this.smartzEventConst.checkConnection:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/check-connection.png'),
              class: 'check-connection',
              text: this.$t('text.check_connection_status')
            }
            break
          case this.smartzEventConst.padCheckAlertWet:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/time-to-change.png'),
              class: 'time-to-change',
              text: this.$t('text.exceed_goal_time')
            }
            break
          default:
            info = {
              imgSrc: '',
              class: 'green-pad',
              text: this.$t('text.dry_diaper')
            }
        }
        return info
      }
    },
    getEventPosition() {
      return (dayItem, index) => {
        let currentEvent = dayItem.Events[index]
        let left = (currentEvent.StartToToday / this.dayDuration) * 100 + '%'
        // If it is an icon at the end of the pad cycle, move it to the right and do not exceed the pad cycle
        if (
          moment(dayItem.EndDate).diff(
            moment(currentEvent.TimeStamp),
            'minutes'
          ) < 30
        ) {
          let endCyclePosition =
            (moment(dayItem.EndDate).diff(
              moment(dayItem.EndDate).format('YYYY-MM-DD 00:00:00'),
              'seconds'
            ) /
              this.dayDuration) *
              100 +
            '%'
          left = `calc(${endCyclePosition} - 24px)`
        }
        return left
      }
    },
    /**
     * Gets the highest priority event from an array of events
     *
     * Priority rules (highest to lowest):
     * 1. `fall` event (highest priority)
     * 2. `red` alert event
     * 3. `allowedTimeExceed` event
     * 4. `safeTimeExceed` event
     * 5. `goalTimeExceed` event
     * 6. Default: last event in array (when no special statuses exist)
     */
    topEventStatus() {
      return events => {
        let topEvent =
          events.find(e => e.Status === smartzEventConst.fall) ||
          events.find(e => e.Status === smartzEventConst.red) ||
          events.find(e => e.Status === smartzEventConst.allowedTimeExceed) ||
          events.find(e => e.Status === smartzEventConst.safeTimeExceed) ||
          events.find(e => e.Status === smartzEventConst.goalTimeExceed) ||
          events[events.length - 1]
        return topEvent ? topEvent.Status : ''
      }
    }
  },
  watch: {
    chartData() {
      this.getCalculatedData()
    }
  },
  methods: {
    getCalculatedData() {
      this.calculatedData = []
      let data = this.spliteCrossDayData()
      data = data.map(item => {
        let date = moment(item.StartDate).format('YYYY-MM-DD')
        return {
          ...item,
          Date: date,
          Events: item.Events.filter(eventItem => {
            return (
              moment(eventItem.TimeStamp).isSameOrBefore(item.EndDate) &&
              moment(eventItem.TimeStamp).isSameOrAfter(item.StartDate)
            )
          }).map(eventItem => {
            return {
              ...eventItem,
              StartToToday: moment(eventItem.TimeStamp).diff(
                moment(date + ' 00:00:00'),
                'seconds'
              )
            }
          }),
          StartToToday: moment(item.StartDate).diff(
            moment(date + ' 00:00:00'),
            'seconds'
          ),
          BlockWidth: moment(item.EndDate).diff(
            moment(item.StartDate),
            'seconds'
          ),
          Feedbacks: item.Feedbacks || [],
          CheckedFeedbackIds:
            item.Feedbacks.filter(f => f.Saved).map(f => f.Id) || [],
          CheckedLeakageFeedbackId: item.LeakageFeedbacks.find(f => f.Saved)
            ? item.LeakageFeedbacks.find(f => f.Saved).Id
            : null
        }
      })

      // group by date
      let startDate = moment(
        this.$store.state.patient.selectedDateRange[0]
      ).format('YYYY-MM-DD')
      let endDate = moment(
        this.$store.state.patient.selectedDateRange[1]
      ).format('YYYY-MM-DD')
      let dateDiff = moment(endDate).diff(startDate, 'days')
      for (let i = 0; i <= dateDiff; i++) {
        let date = moment(startDate)
          .add(i, 'day')
          .format('YYYY-MM-DD')
        this.calculatedData.push({
          Date: date,
          Data: data.filter(item => item.Date === date)
        })
      }
    },
    // Split cross day data so that StartDate and EndDate are on the same day
    spliteCrossDayData() {
      let calculatedData = []
      this.chartData.forEach(item => {
        let dayDiff = moment(
          moment(item.EndDate).format('YYYY-MM-DD 00:00:00')
        ).diff(
          moment(moment(item.StartDate).format('YYYY-MM-DD 00:00:00')),
          'days'
        )
        // Sometimes, the response doesn't have brief removed state at the end of pad cycle.
        // Add the brief removed icon at the end of padcycle if there are no status from the api response
        let allStatus = item.Events
          ? item.Events.map(i => {
              return i.Status
            })
          : []
        if (
          item.IsCompleted === padCycleEndType.isCompleted &&
          !allStatus.includes(smartzEventConst.padRemoved) &&
          this.$parent.selectedFilter.includes(smartzEventConst.padRemoved)
        ) {
          item.Events.push({
            Status: smartzEventConst.padRemoved,
            TimeStamp: item.EndDate
          })
        }
        item = {
          ...item,
          StartDate: moment(item.StartDate).format('YYYY-MM-DD HH:mm:ss'),
          EndDate: moment(item.EndDate).format('YYYY-MM-DD HH:mm:ss'),
          IsStarted: padCycleStartType.isStart,
          Events: this.combineEvents(this.spliteCrossDayEvent(item.Events))
        }
        if (dayDiff === 0) {
          calculatedData.push(item)
        } else {
          for (let i = 0; i <= dayDiff; i++) {
            if (i === 0) {
              calculatedData.push({
                ...item,
                EndDate: moment(item.StartDate).format('YYYY-MM-DD 23:59:59'),
                IsCompleted: padCycleEndType.notCompleted
              })
            } else {
              let startDate = moment(item.StartDate)
                .add(i, 'day')
                .format('YYYY-MM-DD 00:00:00')
              let endDate = moment(item.StartDate)
                .add(i, 'day')
                .format('YYYY-MM-DD 23:59:59')
              calculatedData.push({
                ...item,
                StartDate: startDate,
                EndDate: moment(item.EndDate).isBefore(moment(endDate))
                  ? item.EndDate
                  : endDate,
                IsCompleted: moment(item.EndDate).isBefore(moment(endDate))
                  ? item.IsCompleted
                  : padCycleEndType.notCompleted,
                IsStarted: padCycleStartType.notStart
              })
            }
          }
        }
      })
      return calculatedData
    },
    // Split cross day events. If event cross one day, it will be divided into multiple events according to duration
    spliteCrossDayEvent(events) {
      let calculatedEvents = []
      let isFirstGreen = false
      events.forEach(eventItem => {
        if (!eventItem.Status) return
        let statusArr = [
          smartzEventConst.clumpedGreen,
          smartzEventConst.clumpedYellow,
          smartzEventConst.checkBin
        ].includes(eventItem.Status)
          ? [eventItem.Status]
          : eventItem.Status.split('-')
        let duration = statusArr[1] || 0
        eventItem = {
          Status: statusArr[0],
          Duration: duration,
          TimeStamp: moment(eventItem.TimeStamp).format('YYYY-MM-DD HH:mm:ss'),
          EndTimeStamp: moment(eventItem.TimeStamp)
            .add(duration, 'seconds')
            .format('YYYY-MM-DD HH:mm:ss')
        }

        // mark the first green icon of the pad cycle
        if (!isFirstGreen && eventItem.Status === smartzEventConst.green) {
          isFirstGreen = true
          eventItem.IsFirstGreen = isFirstGreen
        }

        let fromEventToDayEnd = moment(
          moment(eventItem.TimeStamp).format('YYYY-MM-DD 23:59:59')
        ).diff(moment(eventItem.TimeStamp), 'seconds')

        if (eventItem.Duration <= fromEventToDayEnd) {
          calculatedEvents.push(eventItem)
        } else {
          let dayDiff = Math.ceil(eventItem.Duration / this.dayDuration)
          for (let i = 0; i <= dayDiff; i++) {
            let currentEvent = {}
            if (i === 0) {
              currentEvent = {
                ...eventItem,
                EndTimeStamp: moment(eventItem.TimeStamp).format(
                  'YYYY-MM-DD 23:59:59'
                )
              }
            } else {
              let timeStamp = moment(eventItem.TimeStamp)
                .add(i, 'day')
                .format('YYYY-MM-DD 00:00:00')
              let endTimeStamp = moment(eventItem.TimeStamp)
                .add(i, 'day')
                .format('YYYY-MM-DD 23:59:59')
              currentEvent = {
                ...eventItem,
                TimeStamp: timeStamp,
                EndTimeStamp: moment(eventItem.EndTimeStamp).isBefore(
                  moment(endTimeStamp)
                )
                  ? eventItem.EndTimeStamp
                  : endTimeStamp
              }
            }
            currentEvent.Duration = moment(
              moment(currentEvent.EndTimeStamp)
            ).diff(moment(currentEvent.TimeStamp), 'seconds')
            calculatedEvents.push(currentEvent)
          }
        }
      })
      return calculatedEvents
    },
    // When the occurrence time of multiple events is less than 10 minutes, then combine these icons
    combineEvents(events) {
      let combinedEvents = []
      for (let i = 0; i < events.length; i++) {
        let currentEvent = events[i]
        if (i === 0) {
          combinedEvents.push({
            ...currentEvent,
            SubEvents: []
          })
        } else {
          let prevEvent = combinedEvents[combinedEvents.length - 1]
          let diffTime = moment(moment(currentEvent.TimeStamp)).diff(
            moment(prevEvent.TimeStamp),
            'minutes'
          )
          if (
            this.notCombinedEventStatus.includes(prevEvent.Status) ||
            this.notCombinedEventStatus.includes(currentEvent.Status) ||
            diffTime > this.eventDistanceLessShouldCombine
          ) {
            combinedEvents.push({
              ...currentEvent,
              SubEvents: []
            })
          } else {
            combinedEvents[combinedEvents.length - 1].SubEvents.push(
              currentEvent
            )
          }
        }
      }
      return combinedEvents
    }
  }
}
</script>
